/**
 * EventTeamPanel — the event dashboard's Team tab (Eventbrite-style
 * Team Management). Invite co-organizers (full access + public listing +
 * follower announcement) or check-in staff (door access), see roster,
 * remove members.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { UsersRound, Plus, Trash2, Loader2, Crown, ScanLine, Wrench, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });

const ROLES = [
  { value: 'co_organizer', label: 'Co-Organizer', icon: Crown, cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', desc: 'Full access to this event dashboard, listed publicly under Organizers, and their followers get an email announcement.' },
  { value: 'full_manager', label: 'Full Event Manager', icon: Wrench, cls: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30', desc: 'Full access to this event dashboard — not listed publicly.' },
  { value: 'check_in', label: 'Check-in Staff', icon: ScanLine, cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', desc: 'Sees ticket availability + checks guests in via the Team Portal. Access auto-expires 1h after the event.' },
];
const roleMeta = (v) => ROLES.find(r => r.value === v) || ROLES[2];

export default function EventTeamPanel({ eventId }) {
  const [roster, setRoster] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role_template: 'check_in' });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/team/events/${eventId}/roster`, auth());
      setRoster(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load team'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const invite = async () => {
    if (!form.email.includes('@')) { toast.error('Enter their email'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/team/events/${eventId}/invites`, form, auth());
      toast.success(`Invite sent to ${form.email} as ${roleMeta(form.role_template).label}`);
      setForm({ name: '', email: '', role_template: 'check_in' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); }
    setSending(false);
  };

  const removeInvite = async (id) => {
    await axios.delete(`${API}/team/events/${eventId}/invites/${id}`, auth());
    toast.success('Invite cancelled'); load();
  };
  const removeMember = async (id) => {
    await axios.delete(`${API}/team/events/${eventId}/members/${id}`, auth());
    toast.success('Team member removed'); load();
  };

  if (!roster) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>;
  const selMeta = roleMeta(form.role_template);

  return (
    <div className="space-y-5" data-testid="event-team-panel">
      <div>
        <h2 className="font-bold flex items-center gap-2"><UsersRound className="h-4 w-4 text-indigo-400" /> Team Management</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Give venue partners and co-organizers access to this event — just like Eventbrite's Team Management, but with public co-organizer billing.</p>
      </div>

      <Card className="border-indigo-500/30">
        <CardContent className="p-4 space-y-3" data-testid="team-invite-form">
          <div className="grid sm:grid-cols-3 gap-2">
            <Input placeholder="Name (e.g. Chris)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" />
            <Input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="team-email-input" />
            <select value={form.role_template} onChange={e => setForm({ ...form, role_template: e.target.value })}
              className="h-10 px-3 rounded-md border border-border bg-background text-sm" data-testid="team-role-select">
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="team-role-desc">{selMeta.desc}</p>
          <Button size="sm" className="rounded-full font-semibold" disabled={sending} onClick={invite} data-testid="team-invite-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Send invite</>}
          </Button>
        </CardContent>
      </Card>

      {(roster.members || []).length === 0 && (roster.invites || []).length === 0 ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="team-empty">
          No team yet — invite your venue partner or co-organizer above.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {(roster.members || []).map(m => {
            const meta = roleMeta(m.role_template);
            return (
              <Card key={m.member_id} className="border-border" data-testid={`team-member-${m.member_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <meta.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{m.email}</p>
                  </div>
                  <Badge className={`${meta.cls} border text-[9px]`}>{meta.label}</Badge>
                  <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border text-[9px]">Active</Badge>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => removeMember(m.member_id)} data-testid={`team-remove-${m.member_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </CardContent>
              </Card>
            );
          })}
          {(roster.invites || []).map(i => {
            const meta = roleMeta(i.role_template);
            return (
              <Card key={i.invite_id} className="border-dashed border-border" data-testid={`team-invite-${i.invite_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <Mail className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{i.name || i.email}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{i.email}</p>
                  </div>
                  <Badge className={`${meta.cls} border text-[9px]`}>{meta.label}</Badge>
                  <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 border text-[9px]">Invited</Badge>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => removeInvite(i.invite_id)} data-testid={`team-cancel-${i.invite_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
